import React, { useState, useEffect, useMemo } from 'react';
import { 
  FileText, 
  Video, 
  ExternalLink,
  Loader2,
  Calendar as CalendarIcon,
  AlertCircle,
  ChevronDown,
  ChevronRight,
  ChevronLeft,
  ListOrdered,
  Filter,
  Send,
  MessageSquare,
  Copy,
  Check
} from 'lucide-react';

const SMPlanner = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [activeTab, setActiveTab] = useState('article'); // 'article', 'riverside', or 'calendar'
  const [publishMode, setPublishMode] = useState({}); // { video_id: true/false }
  const [publishData, setPublishData] = useState({}); // { video_id: { platform: '', date: '' } }
  const [publishing, setPublishing] = useState({}); // { video_id: true/false }
  const [captionHelper, setCaptionHelper] = useState({}); // { video_id: { loading: false, caption: '', copied: false } }
  
  // Calendar state
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [calendarFilters, setCalendarFilters] = useState({
    article: true,
    riverside: true,
    platforms: {
      facebook: true,
      instagram_reels: true,
      linkedin: true,
      x: true,
      tiktok: true,
      google_business_profile: true,
      youtube_shorts: true
    }
  });
  
  // Filters - separate staged (UI) and applied (actual filtering)
  const [stagedFilters, setStagedFilters] = useState({
    hasContent: 'all',
    platforms: {
      youtube_shorts: 'all',
      facebook: 'all',
      instagram_reels: 'all',
      tiktok: 'all',
      linkedin: 'all',
      x: 'all',
      google_business_profile: 'all'
    },
    // Riverside-specific filters
    mainTitle: 'all',
    videoType: 'all'
  });
  
  const [appliedFilters, setAppliedFilters] = useState({
    hasContent: 'all',
    platforms: {
      youtube_shorts: 'all',
      facebook: 'all',
      instagram_reels: 'all',
      tiktok: 'all',
      linkedin: 'all',
      x: 'all',
      google_business_profile: 'all'
    },
    // Riverside-specific filters
    mainTitle: 'all',
    videoType: 'all'
  });
  
  const [riversideTitles, setRiversideTitles] = useState([]);

  const fetchPlannerItems = React.useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      if (activeTab === 'calendar') {
        // Fetch both article and riverside videos for calendar
        const [articleResponse, riversideResponse] = await Promise.all([
          fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8001'}/api/v6/sm-planner/videos`),
          fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8001'}/api/v6/sm-planner/riverside-videos`)
        ]);
        
        const articleData = await articleResponse.json();
        const riversideData = await riversideResponse.json();
        
        if (articleData.success && riversideData.success) {
          setItems([...articleData.items, ...riversideData.items]);
        } else {
          setError('Failed to fetch planner items');
        }
      } else {
        const endpoint = activeTab === 'riverside' 
          ? '/api/v6/sm-planner/riverside-videos'
          : '/api/v6/sm-planner/videos';
        
        const response = await fetch(
          `${process.env.REACT_APP_API_URL || 'http://localhost:8001'}${endpoint}`
        );
        const data = await response.json();
        
        if (data.success) {
          setItems(data.items);
          
          // Extract unique main titles for Riverside filter
          if (activeTab === 'riverside' && data.items) {
            const titles = [...new Set(data.items.map(item => item.main_title).filter(Boolean))].sort();
            setRiversideTitles(titles);
          } else {
            setRiversideTitles([]);
          }
        } else {
          setError('Failed to fetch planner items');
        }
      }
    } catch (error) {
      console.error('Error fetching planner items:', error);
      setError('Error loading data');
    } finally {
      setLoading(false);
    }
  }, [activeTab]);

  useEffect(() => {
    fetchPlannerItems();
  }, [fetchPlannerItems]);

  const toggleRow = (videoId) => {
    setExpandedRows(prev => {
      const newSet = new Set(prev);
      if (newSet.has(videoId)) {
        newSet.delete(videoId);
      } else {
        newSet.add(videoId);
      }
      return newSet;
    });
  };

  const handleApplyFilters = () => {
    setAppliedFilters({ ...stagedFilters });
  };

  const handleClearFilters = () => {
    const defaultFilters = {
      hasContent: 'all',
      platforms: {
        youtube_shorts: 'all',
        facebook: 'all',
        instagram_reels: 'all',
        tiktok: 'all',
        linkedin: 'all',
        x: 'all',
        google_business_profile: 'all'
      },
      mainTitle: 'all',
      videoType: 'all'
    };
    setStagedFilters(defaultFilters);
    setAppliedFilters(defaultFilters);
  };

  const applyFilters = (itemsList) => {
    return itemsList.filter(item => {
      const hasContent = item.has_content;
      
      // Filter by content generation
      if (appliedFilters.hasContent === 'yes' && !hasContent) return false;
      if (appliedFilters.hasContent === 'no' && hasContent) return false;
      
      // Riverside-specific filters
      if (activeTab === 'riverside') {
        // Filter by main title
        if (appliedFilters.mainTitle !== 'all' && item.main_title !== appliedFilters.mainTitle) {
          return false;
        }
        
        // Filter by video type
        if (appliedFilters.videoType !== 'all' && item.video_type !== appliedFilters.videoType) {
          return false;
        }
      }
      
      // Filter by platform published status
      const hasPlatformFilter = Object.values(appliedFilters.platforms).some(val => val !== 'all');
      
      if (hasPlatformFilter) {
        // If platform filters are active, only include videos WITH content
        if (!hasContent) return false;
        
        const platformStatuses = item.published_status || {};
        
        for (const [platform, filterValue] of Object.entries(appliedFilters.platforms)) {
          if (filterValue === 'all') continue;
          
          const platformStatus = platformStatuses[platform];
          const isPublished = platformStatus?.published || false;
          
          if (filterValue === 'published' && !isPublished) return false;
          if (filterValue === 'unpublished' && isPublished) return false;
        }
      }
      
      return true;
    });
  };

  const filteredItems = applyFilters(items);

  const handlePublishClick = (videoId) => {
    setPublishMode(prev => ({ ...prev, [videoId]: !prev[videoId] }));
    if (!publishMode[videoId]) {
      setPublishData(prev => ({ ...prev, [videoId]: { platform: '', date: '' } }));
    }
  };

  const handlePublish = async (item) => {
    const videoId = item.video_id;
    const publishInfo = publishData[videoId];
    
    if (!publishInfo || !publishInfo.platform || !publishInfo.date) {
      alert('Please select both platform and date');
      return;
    }

    try {
      setPublishing(prev => ({ ...prev, [videoId]: true }));
      
      const requestBody = {
        platform: publishInfo.platform,
        published_date: publishInfo.date
      };
      
      // Add the appropriate ID based on video type
      if (item.is_riverside && item.riverside_video_link_id) {
        requestBody.riverside_video_link_id = item.riverside_video_link_id;
      } else if (!item.is_riverside) {
        // For article videos, video_id is the numeric ID from completed_videos table
        // It should be a number, but handle both number and string cases
        const videoId = typeof item.video_id === 'number' ? item.video_id : parseInt(item.video_id);
        if (!isNaN(videoId) && videoId > 0) {
          requestBody.video_id = videoId;
        } else {
          alert('Unable to determine video ID. Please refresh the page and try again.');
          return;
        }
      } else {
        alert('Unable to determine video type. Please refresh the page and try again.');
        return;
      }
      
      const response = await fetch(
        `${process.env.REACT_APP_API_URL || 'http://localhost:8001'}/api/v6/sm-planner/publish`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(requestBody)
        }
      );
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ detail: 'Unknown error' }));
        throw new Error(errorData.detail || `Server error: ${response.status}`);
      }
      
      const data = await response.json();
      
      if (data.success) {
        // Update the item's published_status in the local state
        setItems(prevItems => 
          prevItems.map(prevItem => {
            if (prevItem.video_id === videoId) {
              return {
                ...prevItem,
                published_status: {
                  ...prevItem.published_status,
                  [publishInfo.platform]: {
                    published: true,
                    published_date: publishInfo.date
                  }
                }
              };
            }
            return prevItem;
          })
        );
        
        // Reset publish mode and data
        setPublishMode(prev => ({ ...prev, [videoId]: false }));
        setPublishData(prev => ({ ...prev, [videoId]: { platform: '', date: '' } }));
      } else {
        alert('Failed to publish: ' + (data.message || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error publishing:', error);
      alert('Error publishing: ' + error.message);
    } finally {
      setPublishing(prev => ({ ...prev, [videoId]: false }));
    }
  };

  const handleCaptionHelper = async (item) => {
    const videoId = item.video_id;
    
    try {
      setCaptionHelper(prev => ({
        ...prev,
        [videoId]: { ...prev[videoId], loading: true, caption: '', copied: false }
      }));

      const requestBody = {
        source_text: item.beats || '', // Script for articles, Show Notes for Riverside
        video_type: item.is_riverside ? 'riverside' : 'article',
        title: item.title || ''
      };

      const response = await fetch(
        `${process.env.REACT_APP_API_URL || 'http://localhost:8001'}/api/v6/sm-planner/caption-helper`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(requestBody)
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ detail: 'Unknown error' }));
        throw new Error(errorData.detail || `Server error: ${response.status}`);
      }

      const data = await response.json();

      if (data.success) {
        setCaptionHelper(prev => ({
          ...prev,
          [videoId]: { ...prev[videoId], loading: false, caption: data.caption, copied: false }
        }));
      } else {
        throw new Error(data.message || 'Failed to generate caption');
      }
    } catch (error) {
      console.error('Error generating caption:', error);
      alert('Error generating caption: ' + error.message);
      setCaptionHelper(prev => ({
        ...prev,
        [videoId]: { ...prev[videoId], loading: false, caption: '', copied: false }
      }));
    }
  };

  const handleCopyCaption = (videoId, caption) => {
    navigator.clipboard.writeText(caption).then(() => {
      setCaptionHelper(prev => ({
        ...prev,
        [videoId]: { ...prev[videoId], copied: true }
      }));
      setTimeout(() => {
        setCaptionHelper(prev => ({
          ...prev,
          [videoId]: { ...prev[videoId], copied: false }
        }));
      }, 2000);
    });
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  // Get published videos grouped by date
  const getPublishedVideosByDate = useMemo(() => {
    const videosByDate = {};
    
    // Combine all items and extract published videos
    items.forEach(item => {
      if (!item.published_status) return;
      
      // Check if item type matches filter
      const isArticle = !item.is_riverside;
      const isRiverside = item.is_riverside;
      
      if (!calendarFilters.article && isArticle) return;
      if (!calendarFilters.riverside && isRiverside) return;
      
      // Check each platform
      Object.entries(item.published_status).forEach(([platform, status]) => {
        if (!status?.published || !status?.published_date) return;
        if (!calendarFilters.platforms[platform]) return;
        
        try {
          const date = new Date(status.published_date);
          // Handle both date strings and datetime strings
          const dateKey = date.toISOString().split('T')[0]; // YYYY-MM-DD
          
          if (!videosByDate[dateKey]) {
            videosByDate[dateKey] = [];
          }
          
          videosByDate[dateKey].push({
            ...item,
            platform,
            publishedDate: status.published_date
          });
        } catch (e) {
          console.error('Error parsing date:', status.published_date, e);
        }
      });
    });
    
    return videosByDate;
  }, [items, calendarFilters]);

  // Calendar navigation
  const changeMonth = (direction) => {
    setCurrentMonth(prev => {
      const newDate = new Date(prev);
      newDate.setMonth(prev.getMonth() + direction);
      return newDate;
    });
  };

  // Get calendar days for current month
  const getCalendarDays = () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();
    
    const days = [];
    
    // Add empty cells for days before month starts
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }
    
    // Add days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(year, month, day));
    }
    
    return days;
  };

  const toggleCalendarFilter = (type, key = null) => {
    if (type === 'article' || type === 'riverside') {
      setCalendarFilters(prev => ({
        ...prev,
        [type]: !prev[type]
      }));
    } else if (type === 'platform' && key) {
      setCalendarFilters(prev => ({
        ...prev,
        platforms: {
          ...prev.platforms,
          [key]: !prev.platforms[key]
        }
      }));
    }
  };

  const renderPlatformBadges = (item) => {
    const platforms = [
      { key: 'youtube_shorts', label: 'YT' },
      { key: 'facebook', label: 'FB' },
      { key: 'instagram_reels', label: 'IG' },
      { key: 'tiktok', label: 'TK' },
      { key: 'linkedin', label: 'LN' },
      { key: 'x', label: 'X' },
      { key: 'google_business_profile', label: 'GB' }
    ];

    // For Riverside videos, always show badges (they can be published without generated content)
    // For article videos, show "No content" only if there's no content AND no published status
    const hasPublishedStatus = item.published_status && Object.keys(item.published_status).length > 0;
    
    // Show badges if it's a Riverside video OR if there's published status OR if there's content
    if (item.is_riverside || hasPublishedStatus || item.has_content) {
      return (
        <div className="flex flex-wrap gap-1">
          {platforms.map(platform => {
            const status = item.published_status?.[platform.key];
            const isPublished = status?.published || false;
            
            return (
              <span
                key={platform.key}
                className={`inline-flex items-center px-2 py-1 rounded text-xs font-semibold ${
                  isPublished 
                    ? 'bg-green-100 text-green-800 border border-green-300' 
                    : 'bg-red-100 text-red-800 border border-red-300'
                }`}
                title={`${platform.label}: ${isPublished ? 'Published' : 'Not Published'}`}
              >
                {platform.label}
              </span>
            );
          })}
        </div>
      );
    }

    // Show "No content" only for article videos without content or published status
    return (
      <div className="flex items-center space-x-1">
        <span className="text-xs text-gray-400 italic">No content</span>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center space-x-3">
          <AlertCircle className="w-5 h-5 text-red-600" />
          <span className="text-red-800">{error}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Social Media Planner</h1>
        <p className="mt-2 text-gray-600">
          Manage and schedule social media content for completed videos
        </p>
      </div>

      {/* Tabs */}
      <div className="mb-6 border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('article')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'article'
                ? 'border-purple-500 text-purple-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Article Videos
          </button>
          <button
            onClick={() => setActiveTab('riverside')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'riverside'
                ? 'border-purple-500 text-purple-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Riverside Videos
          </button>
          <button
            onClick={() => setActiveTab('calendar')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'calendar'
                ? 'border-purple-500 text-purple-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Calendar
          </button>
        </nav>
      </div>

      {/* Calendar View */}
      {activeTab === 'calendar' ? (
        <div className="space-y-6">
          {/* Toggle Filters */}
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <h3 className="text-sm font-semibold text-gray-900 mb-4">Filter Calendar</h3>
            
            {/* Row 1: Article, Riverside */}
            <div className="mb-4">
              <label className="block text-xs font-medium text-gray-700 mb-2">Video Type</label>
              <div className="flex space-x-2">
                <button
                  onClick={() => toggleCalendarFilter('article')}
                  className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                    calendarFilters.article
                      ? 'bg-purple-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  Article
                </button>
                <button
                  onClick={() => toggleCalendarFilter('riverside')}
                  className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                    calendarFilters.riverside
                      ? 'bg-purple-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  Riverside
                </button>
              </div>
            </div>

            {/* Row 2: Platforms */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-2">Platforms</label>
              <div className="flex flex-wrap gap-2">
                {[
                  { key: 'facebook', label: 'Facebook' },
                  { key: 'instagram_reels', label: 'Instagram' },
                  { key: 'linkedin', label: 'LinkedIn' },
                  { key: 'x', label: 'X' },
                  { key: 'tiktok', label: 'TikTok' },
                  { key: 'google_business_profile', label: 'Google Business' },
                  { key: 'youtube_shorts', label: 'YouTube Shorts' }
                ].map(platform => (
                  <button
                    key={platform.key}
                    onClick={() => toggleCalendarFilter('platform', platform.key)}
                    className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                      calendarFilters.platforms[platform.key]
                        ? 'bg-purple-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {platform.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Calendar */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            {/* Month Navigation */}
            <div className="flex items-center justify-between mb-6">
              <button
                onClick={() => changeMonth(-1)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <ChevronLeft className="w-5 h-5 text-gray-600" />
              </button>
              <h2 className="text-xl font-bold text-gray-900">
                {currentMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
              </h2>
              <button
                onClick={() => changeMonth(1)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <ChevronRight className="w-5 h-5 text-gray-600" />
              </button>
            </div>

            {/* Calendar Grid */}
            <div className="grid grid-cols-7 gap-2">
              {/* Day Headers */}
              {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                <div key={day} className="text-center text-xs font-semibold text-gray-500 py-2">
                  {day}
                </div>
              ))}

              {/* Calendar Days */}
              {getCalendarDays().map((day, index) => {
                if (!day) {
                  return <div key={`empty-${index}`} className="min-h-[100px]"></div>;
                }

                const dateKey = day.toISOString().split('T')[0];
                const dayVideos = getPublishedVideosByDate[dateKey] || [];
                const isCurrentMonth = day.getMonth() === currentMonth.getMonth();
                const isToday = dateKey === new Date().toISOString().split('T')[0];

                return (
                  <div
                    key={dateKey}
                    className={`min-h-[100px] border border-gray-200 rounded-lg p-2 ${
                      !isCurrentMonth ? 'bg-gray-50 opacity-50' : 'bg-white'
                    } ${isToday ? 'ring-2 ring-purple-500' : ''}`}
                  >
                    <div className={`text-sm font-medium mb-1 ${
                      isToday ? 'text-purple-600' : 'text-gray-700'
                    }`}>
                      {day.getDate()}
                    </div>
                    <div className="space-y-1 overflow-y-auto max-h-[70px]">
                      {dayVideos.map((video, vidIndex) => (
                        <div
                          key={`${video.video_id}-${video.platform}-${vidIndex}`}
                          onClick={() => {
                            if (video.video_url) {
                              window.open(video.video_url, '_blank', 'noopener,noreferrer');
                            }
                          }}
                          className={`text-xs p-1 bg-purple-50 rounded border border-purple-200 truncate ${
                            video.video_url 
                              ? 'cursor-pointer hover:bg-purple-100 hover:border-purple-300 transition-colors' 
                              : ''
                          }`}
                          title={`${video.title} - ${video.platform}${video.video_url ? ' (Click to open video)' : ''}`}
                        >
                          <div className="font-medium text-purple-900 truncate">
                            {video.title || 'Untitled'}
                          </div>
                          <div className="text-purple-600 text-[10px]">
                            {video.platform.replace('_', ' ')}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      ) : (
        <>
          {/* Stats */}
          <div className="mb-6 bg-gradient-to-r from-purple-50 to-blue-50 rounded-lg p-4 border border-purple-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <Video className="w-6 h-6 text-purple-600" />
                <div>
                  <p className="text-sm text-gray-600">Total Videos</p>
                  <p className="text-2xl font-bold text-gray-900">{items.length}</p>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <div className="text-right">
                  <p className="text-sm text-gray-600">Filtered Results</p>
                  <p className="text-2xl font-bold text-gray-900">{filteredItems.length}</p>
                </div>
              </div>
            </div>
          </div>

      {/* Filters Panel */}
      <div className="mb-6 bg-white rounded-lg border border-gray-200 p-4">
        <h3 className="text-sm font-semibold text-gray-900 mb-4">Filters</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* SM Content Filter */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-2">
              SM Content Generated
            </label>
            <select
              value={stagedFilters.hasContent}
              onChange={(e) => setStagedFilters(prev => ({ ...prev, hasContent: e.target.value }))}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
            >
              <option value="all">All Videos</option>
              <option value="yes">Yes - Has Content</option>
              <option value="no">No - Needs Content</option>
            </select>
          </div>

          {/* Platform Filters */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-2">
              YouTube Shorts
            </label>
            <select
              value={stagedFilters.platforms.youtube_shorts}
              onChange={(e) => setStagedFilters(prev => ({ 
                ...prev, 
                platforms: { ...prev.platforms, youtube_shorts: e.target.value }
              }))}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
            >
              <option value="all">All</option>
              <option value="published">Published</option>
              <option value="unpublished">Not Published</option>
            </select>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-2">
              Facebook
            </label>
            <select
              value={stagedFilters.platforms.facebook}
              onChange={(e) => setStagedFilters(prev => ({ 
                ...prev, 
                platforms: { ...prev.platforms, facebook: e.target.value }
              }))}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
            >
              <option value="all">All</option>
              <option value="published">Published</option>
              <option value="unpublished">Not Published</option>
            </select>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-2">
              Instagram Reels
            </label>
            <select
              value={stagedFilters.platforms.instagram_reels}
              onChange={(e) => setStagedFilters(prev => ({ 
                ...prev, 
                platforms: { ...prev.platforms, instagram_reels: e.target.value }
              }))}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
            >
              <option value="all">All</option>
              <option value="published">Published</option>
              <option value="unpublished">Not Published</option>
            </select>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-2">
              TikTok
            </label>
            <select
              value={stagedFilters.platforms.tiktok}
              onChange={(e) => setStagedFilters(prev => ({ 
                ...prev, 
                platforms: { ...prev.platforms, tiktok: e.target.value }
              }))}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
            >
              <option value="all">All</option>
              <option value="published">Published</option>
              <option value="unpublished">Not Published</option>
            </select>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-2">
              LinkedIn
            </label>
            <select
              value={stagedFilters.platforms.linkedin}
              onChange={(e) => setStagedFilters(prev => ({ 
                ...prev, 
                platforms: { ...prev.platforms, linkedin: e.target.value }
              }))}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
            >
              <option value="all">All</option>
              <option value="published">Published</option>
              <option value="unpublished">Not Published</option>
            </select>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-2">
              X (Twitter)
            </label>
            <select
              value={stagedFilters.platforms.x}
              onChange={(e) => setStagedFilters(prev => ({ 
                ...prev, 
                platforms: { ...prev.platforms, x: e.target.value }
              }))}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
            >
              <option value="all">All</option>
              <option value="published">Published</option>
              <option value="unpublished">Not Published</option>
            </select>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-2">
              Google Business
            </label>
            <select
              value={stagedFilters.platforms.google_business_profile}
              onChange={(e) => setStagedFilters(prev => ({ 
                ...prev, 
                platforms: { ...prev.platforms, google_business_profile: e.target.value }
              }))}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
            >
              <option value="all">All</option>
              <option value="published">Published</option>
              <option value="unpublished">Not Published</option>
            </select>
          </div>
        </div>

        {/* Riverside-specific filters */}
        {activeTab === 'riverside' && (
          <div className="mt-4 pt-4 border-t border-gray-200">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Main Title Filter */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-2">
                  Main Title
                </label>
                <select
                  value={stagedFilters.mainTitle}
                  onChange={(e) => setStagedFilters(prev => ({ ...prev, mainTitle: e.target.value }))}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                >
                  <option value="all">All Titles</option>
                  {riversideTitles.map(title => (
                    <option key={title} value={title}>{title}</option>
                  ))}
                </select>
              </div>

              {/* Video Type Filter */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-2">
                  Video Type
                </label>
                <select
                  value={stagedFilters.videoType}
                  onChange={(e) => setStagedFilters(prev => ({ ...prev, videoType: e.target.value }))}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                >
                  <option value="all">All Types</option>
                  <option value="Full">Full</option>
                  <option value="Segment">Segment</option>
                  <option value="Short">Short</option>
                </select>
              </div>
            </div>
          </div>
        )}

        {/* Filter Action Buttons */}
        <div className="mt-4 flex justify-end space-x-3">
          <button
            onClick={handleClearFilters}
            className="px-4 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Clear Filters
          </button>
          <button
            onClick={handleApplyFilters}
            className="flex items-center space-x-2 px-4 py-2 text-sm text-white bg-purple-600 rounded-lg hover:bg-purple-700"
          >
            <Filter className="w-4 h-4" />
            <span>Apply Filters</span>
          </button>
        </div>
      </div>

      {/* Videos List */}
      {filteredItems.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-12 text-center">
          <Video className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            {items.length === 0 ? 'No completed videos yet' : 'No videos match filters'}
          </h3>
          <p className="text-gray-500">
            {items.length === 0 
              ? 'Completed videos will appear here once they\'re uploaded'
              : 'Try adjusting your filters to see more results'}
          </p>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-12">
                  {/* Expand column */}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Article Title
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Selected Headline
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Platform Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Links
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredItems.map((item) => {
                const isExpanded = expandedRows.has(item.video_id);
                return (
                  <React.Fragment key={item.video_id}>
                    <tr className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-4">
                        <button
                          onClick={() => toggleRow(item.video_id)}
                          className="p-1 hover:bg-gray-200 rounded transition-colors"
                          title={isExpanded ? "Collapse" : "Expand"}
                        >
                          {isExpanded ? (
                            <ChevronDown className="w-4 h-4 text-gray-600" />
                          ) : (
                            <ChevronRight className="w-4 h-4 text-gray-600" />
                          )}
                        </button>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm font-medium text-gray-900">
                          {item.title || 'Untitled'}
                        </div>
                        <div className="text-xs text-gray-500">
                          Doc ID: {item.document_id} | Script ID: {item.script_id || 'N/A'}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-700">
                          {item.selected_headline || (
                            <span className="text-gray-400 italic">No headline</span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        {renderPlatformBadges(item)}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center space-x-3">
                          {item.video_url && (
                            <a
                              href={item.video_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center space-x-1 text-purple-600 hover:text-purple-800 transition-colors"
                              title="View Video"
                            >
                              <Video className="w-4 h-4" />
                              <ExternalLink className="w-3 h-3" />
                            </a>
                          )}
                          {item.pdf_url && (
                            <a
                              href={item.pdf_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center space-x-1 text-blue-600 hover:text-blue-800 transition-colors"
                              title="View PDF"
                            >
                              <FileText className="w-4 h-4" />
                              <ExternalLink className="w-3 h-3" />
                            </a>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500">
                        <div className="flex items-center space-x-1">
                          <CalendarIcon className="w-4 h-4" />
                          <span>{formatDate(item.created_at)}</span>
                        </div>
                      </td>
                    </tr>
                    
                    {/* Expanded content row */}
                    {isExpanded && (
                      <tr>
                        <td colSpan="6" className="px-4 py-4 bg-gray-50">
                          <div className="pl-12 pr-6 space-y-4">
                            {/* Beats Section */}
                            <div className="bg-white rounded-lg p-4 border border-gray-200">
                              <h4 className="text-sm font-semibold text-gray-900 mb-3 flex items-center">
                                <ListOrdered className="w-4 h-4 mr-2 text-purple-600" />
                                {activeTab === 'riverside' ? 'Show Notes' : 'Article Beats'}
                              </h4>
                              {item.beats ? (
                                <div className="text-sm text-gray-700 whitespace-pre-wrap">
                                  {item.beats}
                                </div>
                              ) : (
                                <div className="text-sm text-gray-400 italic">
                                  {activeTab === 'riverside' ? 'No show notes available' : 'No beats available for this article'}
                                </div>
                              )}
                            </div>

                            {/* Caption Helper Section */}
                            <div className="bg-white rounded-lg p-4 border border-gray-200">
                              <div className="flex items-center justify-between mb-4">
                                <h4 className="text-sm font-semibold text-gray-900 flex items-center">
                                  <MessageSquare className="w-4 h-4 mr-2 text-purple-600" />
                                  Caption Helper
                                </h4>
                                <button
                                  onClick={() => handleCaptionHelper(item)}
                                  disabled={!item.beats || captionHelper[item.video_id]?.loading}
                                  className="px-4 py-2 text-sm text-white bg-purple-600 rounded-lg hover:bg-purple-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center space-x-2"
                                >
                                  {captionHelper[item.video_id]?.loading ? (
                                    <>
                                      <Loader2 className="w-4 h-4 animate-spin" />
                                      <span>Generating...</span>
                                    </>
                                  ) : (
                                    <span>Generate Caption</span>
                                  )}
                                </button>
                              </div>

                              {captionHelper[item.video_id]?.caption && (
                                <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
                                  <div className="flex items-center justify-between mb-2">
                                    <label className="text-xs font-medium text-gray-700">
                                      Generated Caption (80-150 words)
                                    </label>
                                    <button
                                      onClick={() => handleCopyCaption(item.video_id, captionHelper[item.video_id].caption)}
                                      className="flex items-center space-x-1 px-2 py-1 text-xs text-gray-600 hover:text-purple-600 transition-colors"
                                      title="Copy to clipboard"
                                    >
                                      {captionHelper[item.video_id]?.copied ? (
                                        <>
                                          <Check className="w-3 h-3" />
                                          <span>Copied!</span>
                                        </>
                                      ) : (
                                        <>
                                          <Copy className="w-3 h-3" />
                                          <span>Copy</span>
                                        </>
                                      )}
                                    </button>
                                  </div>
                                  <div className="text-sm text-gray-700 whitespace-pre-wrap bg-white p-3 rounded border border-gray-200">
                                    {captionHelper[item.video_id].caption}
                                  </div>
                                </div>
                              )}

                              {!item.beats && (
                                <div className="text-xs text-gray-400 italic">
                                  {activeTab === 'riverside' 
                                    ? 'Show notes are required to generate a caption' 
                                    : 'Article beats are required to generate a caption'}
                                </div>
                              )}
                            </div>

                            {/* Publish Section */}
                            <div className="bg-white rounded-lg p-4 border border-gray-200">
                              <div className="flex items-center justify-between mb-4">
                                <h4 className="text-sm font-semibold text-gray-900 flex items-center">
                                  <Send className="w-4 h-4 mr-2 text-purple-600" />
                                  Publish to Platform
                                </h4>
                                {!publishMode[item.video_id] && (
                                  <button
                                    onClick={() => handlePublishClick(item.video_id)}
                                    className="px-4 py-2 text-sm text-white bg-purple-600 rounded-lg hover:bg-purple-700 transition-colors"
                                  >
                                    Publish
                                  </button>
                                )}
                              </div>

                              {publishMode[item.video_id] && (
                                <div className="space-y-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
                                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    {/* Platform Dropdown */}
                                    <div>
                                      <label className="block text-xs font-medium text-gray-700 mb-2">
                                        Platform
                                      </label>
                                      <select
                                        value={publishData[item.video_id]?.platform || ''}
                                        onChange={(e) => setPublishData(prev => ({
                                          ...prev,
                                          [item.video_id]: {
                                            ...prev[item.video_id],
                                            platform: e.target.value,
                                            date: prev[item.video_id]?.date || ''
                                          }
                                        }))}
                                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                                      >
                                        <option value="">Select Platform</option>
                                        <option value="facebook">Facebook</option>
                                        <option value="instagram_reels">Instagram Reels</option>
                                        <option value="linkedin">LinkedIn</option>
                                        <option value="x">X (Twitter)</option>
                                        <option value="tiktok">TikTok</option>
                                        <option value="google_business_profile">Google Business Profile</option>
                                      </select>
                                    </div>

                                    {/* Date Picker */}
                                    <div>
                                      <label className="block text-xs font-medium text-gray-700 mb-2">
                                        Published Date
                                      </label>
                                      <input
                                        type="datetime-local"
                                        value={publishData[item.video_id]?.date || ''}
                                        onChange={(e) => setPublishData(prev => ({
                                          ...prev,
                                          [item.video_id]: {
                                            ...prev[item.video_id],
                                            platform: prev[item.video_id]?.platform || '',
                                            date: e.target.value
                                          }
                                        }))}
                                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                                      />
                                    </div>
                                  </div>

                                  {/* Action Buttons */}
                                  <div className="flex justify-end space-x-3">
                                    <button
                                      onClick={() => {
                                        setPublishMode(prev => ({ ...prev, [item.video_id]: false }));
                                        setPublishData(prev => ({ ...prev, [item.video_id]: { platform: '', date: '' } }));
                                      }}
                                      className="px-4 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                                      disabled={publishing[item.video_id]}
                                    >
                                      Cancel
                                    </button>
                                    <button
                                      onClick={() => handlePublish(item)}
                                      disabled={publishing[item.video_id] || !publishData[item.video_id]?.platform || !publishData[item.video_id]?.date}
                                      className="px-4 py-2 text-sm text-white bg-purple-600 rounded-lg hover:bg-purple-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center space-x-2"
                                    >
                                      {publishing[item.video_id] ? (
                                        <>
                                          <Loader2 className="w-4 h-4 animate-spin" />
                                          <span>Publishing...</span>
                                        </>
                                      ) : (
                                        <>
                                          <Send className="w-4 h-4" />
                                          <span>Publish</span>
                                        </>
                                      )}
                                    </button>
                                  </div>
                                </div>
                              )}

                              {/* Published History */}
                              {item.published_status && Object.keys(item.published_status).some(platform => 
                                item.published_status[platform]?.published
                              ) && (
                                <div className="mt-4 pt-4 border-t border-gray-200">
                                  <h5 className="text-xs font-medium text-gray-700 mb-2">Published History</h5>
                                  <div className="space-y-2">
                                    {Object.entries(item.published_status).map(([platform, status]) => {
                                      if (status?.published) {
                                        const platformLabels = {
                                          youtube_shorts: 'YouTube Shorts',
                                          facebook: 'Facebook',
                                          instagram_reels: 'Instagram Reels',
                                          linkedin: 'LinkedIn',
                                          x: 'X (Twitter)',
                                          tiktok: 'TikTok',
                                          google_business_profile: 'Google Business Profile'
                                        };
                                        return (
                                          <div key={platform} className="flex items-center justify-between text-xs text-gray-600 bg-green-50 px-3 py-2 rounded">
                                            <span className="font-medium">{platformLabels[platform] || platform}</span>
                                            <span>{status.published_date ? new Date(status.published_date).toLocaleString() : 'Date not set'}</span>
                                          </div>
                                        );
                                      }
                                      return null;
                                    })}
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
        </>
      )}
    </div>
  );
};

export default SMPlanner;

